import { surfaceKey } from './materialSurface';
import {
  materialsOfPart, partNamesOf, usePartMaterialNames, usePartMaterials, usePartMaterialsAlso,
} from './partMaterialRun';
import { namedIn, withLetter } from './partsCatalogue';
import { TABLE, TABLE_CELL, TABLE_HEAD, TableCard } from './TableCard';
import { useSubmodelFilter } from './submodelFilterStore';
import { driveText, useToolPairs } from './toolPairs';
import { materialText } from './variantLabel';
import { useLanguage } from '../../contexts/LanguageContext';

// WHICH TOOL TURNS WHICH PIECE OF HARDWARE, AND WHAT ITS TIP IS MADE FOR — the tools
// subphase's table, over the strip its models are made on.
//
// A tool is drawn against the fastener it drives, its tip in that fastener's recess, so a tool is
// only as right as the pair it makes: a hex key modelled at 4 mm for a screw whose socket is 5 is a
// model that looks fine alone and cannot go where it is used. Nothing put the two side by side —
// the tool has a row in the parts list, the screw another, and which one meets which is said only
// sub-step by sub-step.
//
// THE PAIRS ARE READ OFF THE SUB-STEPS, and no call (see toolPairs).
//
// THE TOOL AND ITS HARDWARE FIT BY CONSTRUCTION. They are built together, in one scene, the tip and
// the recesses cut against each other in one script (see toolSet). So there is no tip of the
// tool's own to read, and no fit to judge between two readings: the table says what drives what,
// and by which drive.
//
// AND WHAT THE TOOL IS MADE OF, as the materials table has it — shown, not set here: a tool on
// two is built one mesh a material, and this is where its build is pressed.

export function ToolFitTable({ docId }: { docId: string }) {
  const { t, language } = useLanguage();
  const { items, rows } = useToolPairs(docId);
  const submodel = useSubmodelFilter(docId);
  // Subscribed to the three halves of what a part is on — see materialsOfPart.
  usePartMaterials(docId);
  usePartMaterialsAlso(docId);
  usePartMaterialNames(docId);
  // Each material a line, with which part of the tool it is where that is said.
  const madeOf = (id: string) => {
    const of = partNamesOf(docId, submodel, Number(id));
    return materialsOfPart(docId, submodel, Number(id)).map((pair) => {
      const part = of[surfaceKey(pair)];
      return part ? `${materialText(pair)} (${part})` : materialText(pair);
    });
  };

  const label = (id: string) => {
    const p = items[Number(id)];
    return p ? withLetter(namedIn(p, language) || `#${id}`, p.letter) : `#${id}`;
  };

  return (
    <TableCard title={t('notice.toolFit.title')}>
      {!rows.length ? (
        <p className="text-center text-sm text-slate-500">{t('notice.toolFit.none')}</p>
      ) : (
        // INSIDE THE CARD ON A PHONE: five columns do not fit its width, so the table scrolls
        // sideways within it rather than running past its edge.
        <div className="w-full overflow-x-auto">
        <table className={`${TABLE.replace('w-full', 'w-auto')} mx-auto tabular-nums`}>
          <thead>
            <tr>
              <th className={TABLE_HEAD}>{t('notice.toolFit.tool')}</th>
              <th className={TABLE_HEAD}>{t('notice.toolFit.materials')}</th>
              <th className={TABLE_HEAD}>{t('notice.toolFit.hardware')}</th>
              <th className={TABLE_HEAD}>{t('notice.toolFit.recess')}</th>
              <th className={TABLE_HEAD}>{t('notice.toolFit.subSteps')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const hardware = row.hardware != null ? items[Number(row.hardware)] : undefined;
              const unsaid = <span className="text-slate-400">{t('notice.toolFit.unsaid')}</span>;
              const materials = madeOf(row.tool);
              return (
                <tr key={`${row.tool}|${row.hardware ?? ''}`}>
                  <td className={TABLE_CELL}>{label(row.tool)}</td>
                  <td className={TABLE_CELL}>
                    {materials.length
                      ? materials.map((line) => <div key={line} className="sm:whitespace-nowrap">{line}</div>)
                      : unsaid}
                  </td>
                  <td className={TABLE_CELL}>
                    {row.hardware != null ? label(row.hardware)
                      : <span className="text-amber-600">{t('notice.toolFit.noHardware')}</span>}
                  </td>
                  <td className={TABLE_CELL}>{row.hardware != null && (driveText(hardware) || unsaid)}</td>
                  <td className={TABLE_CELL}>{row.at.join(', ')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}
    </TableCard>
  );
}
